import React, { useState, useEffect } from 'react';
import { Badge, Spinner } from '../../../shared/components/ui';
import { formatCurrency, formatUnitCost, formatNumber } from '../../../shared/utils/formatters';
import { estimatesApi } from '../../../shared/services/estimatesApi';
import { lineItemDescriptionMatchesSources } from '../hooks/useMasterFormatView';

/**
 * LineItemExplanationPanel - Displays AI and user explanations for line items
 *
 * Shows:
 * - AI explanation with confidence score (if AI-estimated)
 * - User adjustment explanation (if user-adjusted)
 * - Similar line items that were used for AI estimation
 * - Message for manually created line items
 */
export function LineItemExplanationPanel({ item, categoryCode }) {
  const [similarItems, setSimilarItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isUploaded = item.costSource === 'uploaded';
  const isManual = !isUploaded && (item.costSource === 'manual' || item.costSource === 'template');
  const hasAIEstimate = item.aiExplanation || item.costSource === 'ai' || item.costSource === 'user_adjusted';
  const isFeeCategory = categoryCode === '00';

  useEffect(() => {
    // Load similar items if we have AI similar item IDs
    if (item.aiSimilarItems && item.aiSimilarItems.length > 0) {
      loadSimilarItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadSimilarItems() {
    setLoading(true);
    setError(null);
    try {
      const response = await estimatesApi.getLineItemsByIds(item.aiSimilarItems);
      setSimilarItems(response.lineItems || []);
    } catch (err) {
      console.error('Failed to load similar items:', err);
      setError('Failed to load similar items');
    } finally {
      setLoading(false);
    }
  }

  // Uploaded line items - show source traceability.
  //
  // Suppress the "Derived from..." panel when the row's description already
  // shows the source name(s). This happens in Custom view (the
  // useMasterFormatView hook rewrites description -> source name), but the
  // check is data-driven so it also covers any mode where the displayed
  // name matches the underlying sources -- redundant info either way.
  if (isUploaded) {
    const hasSources = item.sourceLineItems && item.sourceLineItems.length > 0;
    // Suppress the panel when there's nothing meaningful to add: either the
    // row's description already lists its sources (Custom view rewrite), or
    // we have no source detail at all (the placeholder copy duplicates the
    // "Uploaded" badge already on the row).
    if (lineItemDescriptionMatchesSources(item) || !hasSources) {
      return null;
    }
    return (
      <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-4">
        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="text-sm font-medium">
            {item.sourceLineItems.length === 1 ? (
              <span>
                Derived from {item.sourceLineItems[0].description}
                {item.sourceLineItems[0].totalCost != null && (
                  <> = {formatCurrency(item.sourceLineItems[0].totalCost)}</>
                )}
              </span>
            ) : (
              <div>
                <span>Derived from:</span>
                <ol className="mt-1 ml-4 list-decimal list-inside font-normal">
                  {item.sourceLineItems.map((source, idx) => (
                    <li key={idx}>
                      {source.description}
                      {source.totalCost != null && (
                        <> = {formatCurrency(source.totalCost)}</>
                      )}
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Manual/Template line items - simple message
  if (isManual) {
    return (
      <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg p-4">
        <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-sm font-medium">
            This line item estimate was created manually.
          </span>
        </div>
      </div>
    );
  }

  // AI-estimated or user-adjusted line items
  return (
    <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
      {/* AI Explanation Section */}
      {item.aiExplanation && (
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-2">
            <Badge variant="blue" className="text-xs">AI Estimate</Badge>
            {item.aiEstimatedCost && (
              <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                {isFeeCategory ? `${item.aiEstimatedCost}%` : formatCurrency(item.aiEstimatedCost)}
              </span>
            )}
            {item.aiConfidenceScore && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Confidence: {Math.round(item.aiConfidenceScore * 100)}%
              </span>
            )}
            {item.aiEstimatedAt && (
              <span className="text-xs text-gray-400 dark:text-gray-500">
                {new Date(item.aiEstimatedAt).toLocaleDateString()}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-700 dark:text-gray-200 leading-relaxed">
            {item.aiExplanation}
          </p>
        </div>
      )}

      {/* User Adjustment Section */}
      {item.userAdjustmentReason && (
        <div className={`${item.aiExplanation ? 'border-t border-blue-200 dark:border-blue-800 pt-4 mt-4' : 'mb-4'}`}>
          <div className="flex items-center gap-2 mb-2">
            <Badge variant="yellow" className="text-xs">User Adjusted</Badge>
            {item.userAdjustedCost && (
              <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                {isFeeCategory ? `${item.userAdjustedCost}%` : formatCurrency(item.userAdjustedCost)}
              </span>
            )}
            {item.aiEstimatedCost && item.userAdjustedCost && (isFeeCategory || item.quantity) && (
              (() => {
                // For fee categories, compare percentages directly; for others, compare unit costs
                const aiValue = isFeeCategory ? item.aiEstimatedCost : (item.aiEstimatedCost / item.quantity);
                const userValue = isFeeCategory ? item.userAdjustedCost : (item.userAdjustedCost / item.quantity);
                const variance = ((userValue - aiValue) / aiValue) * 100;
                return (
                  <span className={`text-xs font-medium ${
                    variance > 0 ? 'text-orange-600 dark:text-orange-400' : 'text-green-600 dark:text-green-400'
                  }`}>
                    {variance > 0 ? '+' : ''}
                    {variance.toFixed(1)}%
                  </span>
                );
              })()
            )}
          </div>
          <p className="text-sm text-gray-700 dark:text-gray-200 leading-relaxed">
            {item.userAdjustmentReason}
          </p>
          {item.userAdjustedBy && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Adjusted by {item.userAdjustedBy}
              {item.userAdjustedAt && ` on ${new Date(item.userAdjustedAt).toLocaleDateString()}`}
            </p>
          )}
        </div>
      )}

      {/* Similar Items Table */}
      {hasAIEstimate && item.aiSimilarItems && item.aiSimilarItems.length > 0 && (
        <div className={`${(item.aiExplanation || item.userAdjustmentReason) ? 'border-t border-blue-200 dark:border-blue-800 pt-4 mt-4' : ''}`}>
          <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
            Similar Line Items Used for Estimation ({item.aiSimilarItems.length})
          </h5>

          {loading ? (
            <div className="flex items-center justify-center py-4">
              <Spinner size="sm" />
              <span className="ml-2 text-sm text-gray-600 dark:text-gray-300">Loading similar items...</span>
            </div>
          ) : error ? (
            <div className="text-sm text-red-600 dark:text-red-400 py-2">
              {error}
            </div>
          ) : similarItems.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-xs border border-gray-200 dark:border-gray-800 rounded">
                <thead className="bg-gray-100 dark:bg-gray-800">
                  <tr>
                    <th className="text-left py-2 px-3">Project</th>
                    <th className="text-left py-2 px-3">Description</th>
                    <th className="text-right py-2 px-3">Quantity</th>
                    <th className="text-right py-2 px-3">Unit Cost</th>
                    <th className="text-right py-2 px-3">Total</th>
                    <th className="text-center py-2 px-3">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {similarItems.map((simItem, idx) => (
                    <tr key={simItem.lineItemId || idx} className="border-t border-gray-200 dark:border-gray-800 hover:bg-blue-50 dark:hover:bg-blue-900/30">
                      <td className="py-2 px-3">
                        <div className="max-w-[150px]">
                          <div className="font-medium text-gray-900 dark:text-gray-100 truncate" title={simItem.projectName}>
                            {simItem.projectName}
                          </div>
                          <div className="text-gray-500 dark:text-gray-400 text-xs">
                            {simItem.projectDate}
                          </div>
                        </div>
                      </td>
                      <td className="py-2 px-3">
                        <div className="max-w-[200px] truncate" title={simItem.description}>
                          {simItem.description}
                        </div>
                      </td>
                      <td className="py-2 px-3 text-right text-gray-700 dark:text-gray-200">
                        {formatNumber(simItem.quantity)} {simItem.unit}
                      </td>
                      <td className="py-2 px-3 text-right font-medium text-gray-900 dark:text-gray-100">
                        {formatUnitCost(simItem.unitCost)}
                      </td>
                      <td className="py-2 px-3 text-right text-gray-900 dark:text-gray-100">
                        {formatCurrency(simItem.totalCost)}
                      </td>
                      <td className="py-2 px-3 text-center">
                        {simItem.costSource === 'ai' && (
                          <Badge variant="blue" className="text-xs">AI</Badge>
                        )}
                        {simItem.costSource === 'user_adjusted' && (
                          <Badge variant="yellow" className="text-xs">Adj</Badge>
                        )}
                        {simItem.costSource === 'manual' && (
                          <Badge variant="gray" className="text-xs">Manual</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Cost Analysis Summary */}
              {similarItems.length > 0 && (
                <div className="mt-3 p-3 bg-white dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-800">
                  <h6 className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-2">Cost Analysis</h6>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                    <div>
                      <p className="text-gray-500 dark:text-gray-400">Your Unit Cost</p>
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        {item.unitCost ? formatUnitCost(item.unitCost) : '—'}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500 dark:text-gray-400">Avg Similar Cost</p>
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        {formatUnitCost(
                          similarItems.reduce((sum, s) => sum + (s.unitCost || 0), 0) / similarItems.length
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500 dark:text-gray-400">Min Cost</p>
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        {formatUnitCost(Math.min(...similarItems.map(s => s.unitCost || 0)))}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500 dark:text-gray-400">Max Cost</p>
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        {formatUnitCost(Math.max(...similarItems.map(s => s.unitCost || 0)))}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400 py-2">
              No similar items found.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default LineItemExplanationPanel;
